import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { useQueryParams, useNavigationPrompt } from 'raviger';
import { Formik } from 'formik';
import { Form as BootstrapForm, ButtonGroup, Card, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Yup from 'yup';
import Scrollbar from '../components/Scrollbars';
import { AddressSearch, Checkbox, Radio } from '../components/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft, faClipboardList, faUserAlt, faUserAltSlash } from '@fortawesome/free-solid-svg-icons';
import { capitalize } from '../utils/formatString';
import ButtonSpinner from '../components/ButtonSpinner';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';

// Form for owner and reporter Person objects.
const AddressForm = (props) => {

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    animal_id = '',
    owner_id = '',
  } = queryParams;

  // Determine if this is an owner or reporter when creating a Person.
  let is_owner = (window.location.pathname.includes("/people/owner/") || window.location.pathname.endsWith("/workflow/owner") || window.location.pathname.endsWith("/workflow/return") || (owner_id ? true : false));

  // Determine if this is an intake workflow.
  let is_intake = window.location.pathname.includes("/intake/workflow/");

  // Determine if this is an return animal workflow.
  let is_return = window.location.pathname.endsWith("/workflow/return");

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const initial_coordinates = state?.incident?.coordinates ?? [0, 0];

  useNavigationPrompt(true, "Are you sure you would like to leave the animal intake workflow? No data will be saved.");

  const [data, setData] = useState(props.state.steps.initial);
  const [existingOwner, setExistingOwner] = useState({id:'', animals:[]});
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [existingRequest, setExistingRequest] = useState({id:''});
  const [existingOwners, setExistingOwners] = useState([]);
  const [existingOwnerOptions, setExistingOwnerOptions] = useState([]);
  const [existingRequests, setExistingRequests] = useState([]);

  const fetchExistingServiceRequestData = async (address, city, address_state) => {
    await axios.get('/hotline/api/servicerequests/?no_page=true&incident=' + props.incident + '&organization='+ props.organization + '&exclude_status=canceled&light=true&address=' + address + '&city=' + city + '&state=' + address_state)
    .then(response => {
      setExistingRequests(response.data);
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const fetchExistingOwnerData = async (address, city, address_state, phone=null) => {
    let lookup_params = '';
    if (phone) {
      lookup_params = '&phone=' + phone;
    }
    else {
      lookup_params = '&address=' + address + '&city=' + city + '&state=' + address_state;
    }
    await axios.get('/people/api/person/?no_page=true&light=true&incident=' + props.incident + '&organization=' + props.organization +'&training=' + state.incident.training + '&only_inactive=' + (is_intake || is_return ? 'false' : 'true') + lookup_params)
    .then(response => {
      if (owner_id){
        let owner_data = response.data.filter(owner => owner.id === Number(owner_id))[0];
        const ids = new Set();
        setExistingOwnerOptions(response.data.filter(owner => owner.display_phone && (!ids.has(owner.display_phone) && ids.add(owner.display_phone))).map(owner => ({value:owner.id, label:owner.display_phone })))
        setExistingOwner(owner_data);
      }
      setExistingOwners(response.data.filter(owner => owner.display_phone));
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  // Resolve a known owner_id (e.g. the return-animal workflow) directly, without fetching the full owner list.
  // useEffect(() => {
  //   let unmounted = false;
  //   if (owner_id) {
  //     axios.get('/people/api/person/' + owner_id + '/?incident=' + props.incident)
  //     .then(response => {
  //       if (!unmounted) {
  //         setExistingOwner(response.data);
  //       }
  //     })
  //     .catch(error => {
  //       if (!unmounted) {
  //         setShowSystemError(true);
  //       }
  //     });
  //   }
  //   return () => { unmounted = true; };
  // }, [owner_id]);

  return (
    <>
      <Formik
        initialValues={data}
        enableReinitialize={true}
        validationSchema={Yup.object({
          address: Yup.string(),
          apartment: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          city: Yup.string(),
          state: Yup.string(),
          zip_code: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          latitude: Yup.number()
            .nullable(),
          longitude: Yup.number()
            .nullable(),
        })}
        onSubmit={(values, { setSubmitting, setFieldValue, resetForm }) => {
          // If we are returning an animal with a SR.
          if (animal_id && existingRequest.id) {
            axios.get('/hotline/api/incident/' + state.incident.id + '/servicerequests/' + existingRequest.id_for_incident + '/', {
            })
            .then(response => {
              response.data.animals = [...response.data.animals, ...props.state.steps.animals];
              props.onSubmit('initial', values, is_owner ? 'owners' : 'reporter', response.data);
            })
            .catch(error => {
              setShowSystemError(true);
            });
          }
          // If we are returning an animal.
          else if (animal_id) {
            // Add animal current owners on submit.
            let existing_owners = [...props.state.steps.owners.map(owner => ({...owner, matching_owner:true}))];
            values['owner_objects'] = existing_owners;
            props.onSubmit('initial', existingOwner.id ? existingOwner : values, owner_id ? 'owners' : 'reporter');
          }
          // If we have an existing SR.
          else if (existingRequest.id) {
            axios.get('/hotline/api/incident/' + state.incident.id + '/servicerequests/' + existingRequest.id_for_incident + '/', {
            })
            .then(response => {
              props.onSubmit('initial', values, is_owner ? 'owners' : 'reporter', response.data);
            })
            .catch(error => {
              setShowSystemError(true);
            });
          }
          // If we have an existing owner but haven't selected animals yet and we aren't in a return workflow.
          else if (!is_return && existingOwner.id && !existingOwner.pre_reg_animals) {
            axios.get('/people/api/pre-reg-person/' + existingOwner.id + '/?incident=' + props.incident, {
            })
            .then(response => {
              setSubmitting(false);
              response.data["matching_owner"] = true;
              setExistingOwner(response.data);
              setFieldValue("address", response.data.address);
              setFieldValue("apartment", response.data.apartment);
              setFieldValue("city", response.data.city);
              setFieldValue("state", response.data.state);
              setFieldValue("zip_code", response.data.zip_code);
              setFieldValue("matching_owner", true);

              // If the person does not have any animals to select from, automatically move to the next step.
              if (response.data.pre_reg_animals.filter(pre_reg_animal => !response.data.animals.map(existing_animal => existing_animal.pre_reg).includes(pre_reg_animal.id)).length === 0) {
                response.data['animals'] = [];
                props.onSubmit('initial', response.data, is_owner ? 'owners' : 'reporter');
              }
            })
            .catch(error => {
              setShowSystemError(true);
            });
          }
          // If we have an existing owner and selected animals.
          else if (existingOwner.id && existingOwner.pre_reg_animals) {
            let existingOwnerCopy = {...existingOwner};
            let animals = existingOwnerCopy.animals.filter(animal => selectedAnimals.includes(Number(animal.pre_reg)))
            animals = animals.concat(existingOwnerCopy.pre_reg_animals.filter(animal => !animals.map(animal=> animal.pre_reg).includes(animal.id) && selectedAnimals.includes(Number(animal.id))));
            existingOwnerCopy.animals = animals;
            existingOwnerCopy.animals.forEach(animal => animal.status = 'REPORTED');
            existingOwnerCopy.animals.forEach(animal => animal.incident = state.incident.id);
            props.onSubmit('initial', existingOwnerCopy, is_owner ? 'owners' : 'reporter');
          }
          else {
            props.onSubmit('initial', values, is_owner ? 'owners' : 'reporter');
          }
        }}
      >
        {formikProps => (
          <Card border="secondary" className={"mt-3"}>
            <Card.Header as="h5" className="ps-3"><span id="back" style={{cursor:'pointer'}} onClick={() => window.history.back()} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>Lookup Address</Card.Header>
            <Card.Body>
              <AddressSearch formikProps={formikProps} fetchExistingServiceRequestData={fetchExistingServiceRequestData} fetchExistingOwnerData={fetchExistingOwnerData} initialData={props.state.steps.initial} label={is_return ? "Search for Return Animal Address" : is_intake ? "Search for " + (is_owner ? "Owner Phone or Address" : "Animal Found Address") : "Search for Service Request/Owner Address"} incident={props.incident} initial_coordinates={initial_coordinates} show_apt={true} address_form={false} show_same={true} error="Address was not selected." existingOwner={existingOwner} existingOwners={existingOwners} existingOwnerOptions={existingOwnerOptions} setExistingOwner={setExistingOwner} setSelectedAnimals={setSelectedAnimals} always_show={true} is_intake={is_intake} is_owner={is_owner} />
              {formikProps.values.address && existingOwner.pre_reg_animals && existingOwner.pre_reg_animals.length > 0 ?
              <span>
                <h4 className="mt-3">Use Matching Owner</h4>
                <Col xs={9} className="border rounded mb-3" style={{marginLeft:"1px", height:"59px", overflowY:"hidden", paddingRight:"-1px"}}>
                  <div className="mt-1 mb-1">
                    <div className="card-header rounded" style={{paddingLeft:"12px", marginLeft:"5px", marginRight:"5px", height:"50px"}}>
                      <Radio
                        id={String(existingOwner.id)}
                        name={String(existingOwner.id)}
                        checked={true}
                        style={{
                          transform: "scale(1.25)",
                          marginLeft: "-14px",
                          marginTop: "-5px",
                          marginBottom: "-5px",
                        }}
                      />
                      {existingOwner.first_name} {existingOwner.last_name} - {existingOwner.display_phone} - {existingOwner.address}{existingOwner.apartment ? <span> Apt #{existingOwner.apartment}</span> : ""} ({existingOwner.pre_reg_animal_count || 0} Animal{existingOwner.pre_reg_animal_count === 1 ? "" : "s"})
                    </div>
                  </div>
                </Col>
                <h4>Select Which Animals To Use</h4>
                <Col xs={9} className="border rounded" style={{paddingTop:"7px", paddingBottom:"2px"}}>
                {existingOwner.pre_reg_animals.filter(pre_reg_animal => !existingOwner.animals.map(existing_animal => existing_animal.pre_reg).includes(pre_reg_animal.id)).map((animal, index) => (
                  <div key={animal.id} style={{marginLeft:"6px"}}>
                    <Checkbox
                      name={`animal_ids`}
                      checked={selectedAnimals.includes(animal.id) || false}
                      onChange={() => {
                        if (selectedAnimals.includes(animal.id)) {
                          let newItems = [...selectedAnimals];
                          newItems = newItems.filter(id => id !== animal.id);
                          setSelectedAnimals(newItems);
                          // formikProps.setFieldValue(`animal_ids`, newItems);
                        }
                        else {
                          let newItems = [...selectedAnimals];
                          newItems.push(animal.id);
                          setSelectedAnimals(newItems);
                          // formikProps.setFieldValue(`animal_ids`, newItems);
                        }
                      }}
                      style={{
                        transform: "scale(1.5)",
                        marginTop: "-4px",
                        marginLeft:"-4px"
                      }}
                    />
                    {animal.animal_count > 1 ? <span>{animal.animal_count} {capitalize(animal.species_string)} -&nbsp;
                    {existingOwner.animals.filter(existing_animal => existing_animal.pre_reg === animal.id).length ? existingOwner.animals.filter(existing_animal => existing_animal.pre_reg === animal.id)[0].status : "PREREGISTERED"}</span> : <span>{animal.name||"Unknown Name"}&nbsp;-&nbsp;{capitalize(animal.species_string)} - {existingOwner.animals.filter(existing_animal => existing_animal.pre_reg === animal.id).length ? existingOwner.animals.filter(existing_animal => existing_animal.pre_reg === animal.id)[0].status : "PREREGISTERED"}</span>}
                    <OverlayTrigger
                      key={"animal-details"}
                      placement="top"
                      overlay={
                        <Tooltip id={"tooltip-animal-details"} style={{textTransform: "capitalize"}}>
                          Gender: {animal.sex ? animal.sex : "N/A"} <br />
                          Size: {animal.size ? animal.size : "N/A"} <br />
                          Color: {animal.pcolor ? animal.pcolor : "N/A"}{animal.scolor ? "/" + animal.scolor : ""} <br />
                          Breed/Description: {animal.color_notes ? animal.color_notes : "N/A"} <br />
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faClipboardList} className="ms-1" inverse />
                    </OverlayTrigger>
                    </div>
                ))}
              </Col>
              </span> :
              <span>
              {!is_intake ? <h4 className="mt-3">Use Matching Service Request</h4> : ""}
              {!is_intake ? <Col xs={9} className="border rounded" style={{marginLeft:"1px", height:existingRequests.filter(request => formikProps.values.address && request.address === formikProps.values.address && request.city === formikProps.values.city && request.state === formikProps.values.state).length === 0 ? "59px" : "115px", overflowY:"auto", paddingRight:"-1px"}}>
                <Scrollbar no_shadow="true" style={{height:existingRequests.filter(request => formikProps.values.address && request.address === formikProps.values.address && request.city === formikProps.values.city && request.state === formikProps.values.state).length === 0 ? "57px" : "113px", marginLeft:"5px", marginRight:"5px", paddingRight:"0px", paddingBottom:"0px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  {existingRequests.filter(request => formikProps.values.address && request.address === formikProps.values.address && request.city === formikProps.values.city && request.state === formikProps.values.state).map(service_request => (
                    <div key={service_request.id} className="mt-1 mb-1" style={{height:"50px"}}>
                      <div className="card-header rounded" style={{paddingLeft:"12px", height:"50px"}}>
                        <Radio
                          id={String(service_request.id)}
                          name={String(service_request.id)}
                          checked={existingRequest.id === service_request.id}
                          style={{
                            transform: "scale(1.25)",
                            marginLeft: "-20px",
                            marginTop: "-8px",
                            marginBottom: "-5px"
                          }}
                          onChange={(e) => {
                            setExistingRequest(existingRequest.id === service_request.id ? {id:''} : service_request);
                            setExistingOwner({id:'', animals:[]});
                          }}
                        />
                        <span style={{marginTop:"1px", position:"absolute"}}>SR#{service_request.id_for_incident} - {capitalize(service_request.status)} - {service_request.full_address}
                        {service_request.owner_names.length === 0 ?
                          <OverlayTrigger
                            key={"stray"}
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-stray`}>
                                Animal is stray
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon icon={faUserAltSlash} className="ms-1 me-1" size="sm" />
                          </OverlayTrigger> :
                          <OverlayTrigger
                            key={"owners"}
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-owners`}>
                                Owners:
                                {service_request.owner_names.map(owner_name => (
                                  <div key={owner_name}>{owner_name}</div>
                                ))}
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon icon={faUserAlt} className="ms-1 me-1" size="sm" />
                          </OverlayTrigger>
                        }
                        ({service_request.animal_count || 0} Animal{service_request.animal_count === 1 ? '' : 's'})
                        </span>
                      </div>
                    </div>
                  ))}
                  {!formikProps.values.address || existingRequests.filter(request => formikProps.values.address && request.address === formikProps.values.address && request.city === formikProps.values.city && request.state === formikProps.values.state).length === 0 ? <div className="d-flex card-header mt-1 mb-1 rounded align-items-center" style={{height:"50px"}}>
                    No matching Service Requests found.
                  </div> : ""}
                </Scrollbar>
              </Col> : ""}
              <h4 className={"mt-3"}>Use Matching Owner</h4>
              <Col xs={9} className="border rounded" style={{marginLeft:"1px", height:existingOwners.filter(request => (formikProps.values.display_phone === request.display_phone) || (formikProps.values.address && request.address === formikProps.values.address && request.city === formikProps.values.city && request.state === formikProps.values.state)).length === 0 ? "59px" : "115px", overflowY:"auto", paddingRight:"-1px"}}>
                <Scrollbar no_shadow="true" style={{height:existingOwners.filter(request => (formikProps.values.display_phone === request.display_phone) || (formikProps.values.address && request.address === formikProps.values.address && request.city === formikProps.values.city && request.state === formikProps.values.state)).length === 0 ? "57px" : "113px", marginLeft:"5px", marginRight:"5px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  {existingOwners.filter(owner => (owner.display_phone === formikProps.values.display_phone) || (formikProps.values.address && owner.address === formikProps.values.address && owner.city === formikProps.values.city && owner.state === formikProps.values.state)).map(owner => (
                    <div key={owner.id} className="mt-1 mb-1">
                      <div className="card-header rounded" style={{paddingLeft:"12px", height:"50px"}}>
                        <Radio
                          id={String(owner.id)}
                          name={String(owner.id)}
                          checked={Number(existingOwner.id) === owner.id}
                          style={{
                            transform: "scale(1.25)",
                            marginLeft: "-14px",
                            marginTop: "-5px",
                            marginBottom: "-5px"
                          }}
                          onChange={() => {
                            setExistingOwner(existingOwner.id === owner.id ? {id:'', animals:[]} : owner);
                            setExistingRequest({id:''})}}
                        />
                        <span style={{marginTop:"3px", position:"absolute"}}>{owner.first_name} {owner.last_name} - {owner.display_phone} - {owner.address}{owner.apartment ? <span> Apt #{owner.apartment}</span> : ""} ({owner.pre_reg_animal_count || 0} Animal{owner.pre_reg_animal_count === 1 ? "" : "s"})</span>
                      </div>
                    </div>
                  ))}
                  {existingOwners.filter(request => (formikProps.values.display_phone === request.display_phone) || (formikProps.values.address && request.address === formikProps.values.address && request.city === formikProps.values.city && request.state === formikProps.values.state)).length === 0 ?<div className="d-flex card-header mt-1 mb-1 rounded align-items-center" style={{height:"50px"}}>
                    No matching Owners found.
                  </div> : ""}
                </Scrollbar>
              </Col>
              </span>}
            </Card.Body>
            <ButtonGroup size="lg" >
              {existingOwner.id && !existingOwner.animals ? 
              <ButtonSpinner disabled={!formikProps.values.address && !existingOwner.id} type="button" className="btn btn-primary border" onClick={() => { formikProps.submitForm(); }}>
                {is_return ? "Next Step" : "Make Selection"}
              </ButtonSpinner>
              :
              <ButtonSpinner isSubmitting={formikProps.isSubmitting} disabled={!formikProps.values.address} isSubmittingText="Loading..." type="button" className="btn btn-primary border" onClick={() => { formikProps.submitForm(); }}>
                Next Step
              </ButtonSpinner>}
            </ButtonGroup>
          </Card>
        )}
      </Formik>
    </>
  );
};

export default AddressForm;
